import React, { useState } from 'react';
import { getPlatformClass } from '../../utils/platformUtils';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import sharedStyles from './InventoryForm.module.css';

const StockOutForm = ({
  products = [],
  onSave,
  onCancel,
  className = '',
  ...props
}) => {
  const platformClass = getPlatformClass();

  // Form state
  const [formData, setFormData] = useState({
    productId: '',
    variantId: '', // For complex products
    challanNumber: '',
    partyName: '',
    quantity: '',
    reason: '',
    notes: ''
  });

  // Form validation
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get selected product and variant
  const selectedProduct = products.find(p => p.id === formData.productId);
  const selectedVariant = selectedProduct?.hasVariants && selectedProduct.variants 
    ? selectedProduct.variants.find(v => v.id === formData.variantId)
    : null;

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Reset variant when product changes
    if (field === 'productId') {
      setFormData(prev => ({
        ...prev,
        variantId: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.productId) {
      newErrors.productId = 'Please select a product';
    }

    // For complex products, require variant selection
    if (selectedProduct?.hasVariants && !formData.variantId) {
      newErrors.variantId = 'Please select a variant';
    }

    if (!formData.challanNumber || formData.challanNumber.trim() === '') {
      newErrors.challanNumber = 'Please enter a challan number';
    }

    if (!formData.partyName || formData.partyName.trim() === '') {
      newErrors.partyName = 'Please enter a customer name';
    }

    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = 'Please enter a valid quantity';
    }

    if (formData.quantity && parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (formData.quantity && parseFloat(formData.quantity) > 999999) {
      newErrors.quantity = 'Quantity is too large';
    }

    // Check if trying to remove more than available stock
    const currentStock = getCurrentStock();
    if (formData.quantity && parseFloat(formData.quantity) > currentStock) {
      newErrors.quantity = `Cannot remove more than available stock (${currentStock} ${getUnit()})`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const stockOutData = {
        productId: formData.productId,
        variantId: formData.variantId || null,
        quantity: parseFloat(formData.quantity),
        productName: selectedProduct.name,
        variantName: selectedVariant ? (selectedVariant.variantName || `${selectedVariant.size} - ${selectedVariant.color}${selectedVariant.thickness ? ` - ${selectedVariant.thickness}` : ''}`) : null,
        previousStock: selectedProduct.hasVariants 
          ? selectedVariant.currentStock 
          : selectedProduct.currentStock,
        challanNumber: formData.challanNumber,
        partyName: formData.partyName,
        reason: formData.reason,
        notes: formData.notes
      };

      await onSave(stockOutData);
      
      // Reset form
      setFormData({
        productId: '',
        variantId: '',
        challanNumber: '',
        partyName: '',
        quantity: '',
        reason: '',
        notes: ''
      });
      setErrors({});
    } catch (error) {
      setErrors({ submit: 'Failed to save stock out. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormData({
      productId: '',
      variantId: '',
      challanNumber: '',
      partyName: '',
      quantity: '',
      reason: '',
      notes: ''
    });
    setErrors({});
    onCancel?.();
  };

  // Get current stock for display
  const getCurrentStock = () => {
    if (selectedProduct?.hasVariants && selectedVariant) {
      return selectedVariant.currentStock;
    } else if (selectedProduct && !selectedProduct.hasVariants) {
      return selectedProduct.currentStock;
    }
    return 0;
  };

  // Get unit for display
  const getUnit = () => {
    return selectedProduct?.unit || 'units';
  };

  // Get remaining stock for display
  const getRemainingStock = () => {
    const current = getCurrentStock();
    const quantity = parseFloat(formData.quantity) || 0;
    return current - quantity;
  };

  // Get stock status for visual indicator
  const getStockStatus = () => {
    const currentStock = getCurrentStock();
    const minStock = selectedProduct?.hasVariants && selectedVariant 
      ? selectedVariant.minStock 
      : selectedProduct?.minStock || 0;

    if (currentStock <= minStock) return 'low';
    if (currentStock <= minStock * 1.5) return 'warning';
    return 'good';
  };

  const stockStatus = selectedProduct ? getStockStatus() : 'unknown';

  return (
    <div className={`${sharedStyles.formWrapper} ${platformClass} ${className}`} {...props}>
      <div className={sharedStyles.formHeader}>
        <PlatformText variant="h3" className={sharedStyles.formTitle}>
          Remove Stock
        </PlatformText>
        <PlatformText variant="body" className={sharedStyles.formSubtitle}>
          Remove stock from your inventory
        </PlatformText>
      </div>

      <form onSubmit={handleSubmit} className={`${sharedStyles.formContent} form-inventory`}>
        {/* Product Selection */}
        <div className={sharedStyles.field}>
          <label htmlFor="product-select" className={sharedStyles.formLabel}>
            <PlatformText variant="body" className={sharedStyles.labelText}>
              Select Product *
            </PlatformText>
          </label>
          <select
            id="product-select"
            value={formData.productId}
            onChange={(e) => handleInputChange('productId', e.target.value)}
            className={`${sharedStyles.select} ${errors.productId ? 'error' : ''}`}
            required
          >
            <option value="">Choose a product...</option>
            {products.map(product => {
              const stockDisplay = product.hasVariants 
                ? `${product.variants?.length || 0} variants`
                : `${product.currentStock || 0} ${product.unit || 'units'}`;
              
              return (
                <option key={product.id} value={product.id}>
                  {product.name} ({stockDisplay})
                  {product.hasVariants && ' (Variants)'}
                </option>
              );
            })}
          </select>
          {errors.productId && (
            <PlatformText variant="caption" className="error-text">
              {errors.productId}
            </PlatformText>
          )}
        </div>

        {/* Variant Selection for Complex Products */}
        {selectedProduct?.hasVariants && (
          <div className={sharedStyles.field}>
            <label htmlFor="variant-select" className={sharedStyles.formLabel}>
              <PlatformText variant="body" className={sharedStyles.labelText}>
                Select Variant *
              </PlatformText>
            </label>
            <select
              id="variant-select"
              value={formData.variantId}
              onChange={(e) => handleInputChange('variantId', e.target.value)}
              className={`${sharedStyles.select} ${errors.variantId ? 'error' : ''}`}
              required
            >
              <option value="">Choose a variant...</option>
              {selectedProduct.variants?.map(variant => (
                <option key={variant.id} value={variant.id}>
                  {variant.variantName || `${variant.size} - ${variant.color}${variant.thickness ? ` - ${variant.thickness}` : ''}`} (Available: {variant.currentStock || 0})
                </option>
              ))}
            </select>
            {errors.variantId && (
              <PlatformText variant="caption" className="error-text">
                {errors.variantId}
              </PlatformText>
            )}
          </div>
        )}

        {/* Selected Product/Variant Info */}
        {selectedProduct && (
          <div className={`selected-product-info ${stockStatus}`}>
            <div className="product-details">
              <PlatformText variant="h4" className="product-name">
                {selectedProduct.name}
                {selectedProduct.hasVariants && selectedVariant && (
                  <span className="variant-name">
                    {' '}- {selectedVariant.variantName || `${selectedVariant.size} - ${selectedVariant.color}${selectedVariant.thickness ? ` - ${selectedVariant.thickness}` : ''}`}
                  </span>
                )}
              </PlatformText>
              <PlatformText variant="body" className="product-category">
                {selectedProduct.category}
                {selectedProduct.hasVariants && (
                  <span className="product-type"> (Variants)</span>
                )}
              </PlatformText>
            </div>
            <div className="stock-info">
              <div className="current-stock">
                <PlatformText variant="caption" className="stock-label">
                  Available Stock
                </PlatformText>
                <PlatformText variant="h4" className={`stock-value ${stockStatus}`}>
                  {getCurrentStock()} {getUnit()}
                </PlatformText>
              </div>
              {!selectedProduct.hasVariants && (
                <div className="min-stock">
                  <PlatformText variant="caption" className="stock-label">
                    Min Stock
                  </PlatformText>
                  <PlatformText variant="body" className="stock-value">
                    {selectedProduct.minStock || 0} {getUnit()}
                  </PlatformText>
                </div>
              )}
              {selectedProduct.hasVariants && selectedVariant && (
                <div className="min-stock">
                  <PlatformText variant="caption" className="stock-label">
                    Min Stock
                  </PlatformText>
                  <PlatformText variant="body" className="stock-value">
                    {selectedVariant.minStock || 0} {getUnit()}
                  </PlatformText>
                </div>
              )}
            </div>
            {/* Stock Status Indicator */}
            <div className="stock-status-indicator">
              <PlatformText variant="caption" className={`status-text ${stockStatus}`}>
                {stockStatus === 'low' && '⚠️ Low Stock'}
                {stockStatus === 'warning' && '⚠️ Stock Warning'}
                {stockStatus === 'good' && '✅ Stock Good'}
              </PlatformText>
            </div>
          </div>
        )}

        {/* Challan Number and Customer Name Row */}
        <div className={sharedStyles.formRow}>
          <div className={sharedStyles.field}>
            <label htmlFor="challan-number-input" className={sharedStyles.formLabel}>
              <PlatformText variant="body" className={sharedStyles.labelText}>
                Challan Number *
              </PlatformText>
            </label>
            <input
              id="challan-number-input"
              type="text"
              value={formData.challanNumber}
              onChange={(e) => handleInputChange('challanNumber', e.target.value)}
              className={`${sharedStyles.input} ${errors.challanNumber ? 'error' : ''}`}
              placeholder="Enter challan number"
              required
            />
            {errors.challanNumber && (
              <PlatformText variant="caption" className="error-text">
                {errors.challanNumber}
              </PlatformText>
            )}
          </div>

          <div className={sharedStyles.field}>
            <label htmlFor="party-name-input" className={sharedStyles.formLabel}>
              <PlatformText variant="body" className={sharedStyles.labelText}>
                Customer Name *
              </PlatformText>
            </label>
            <input
              id="party-name-input"
              type="text"
              value={formData.partyName}
              onChange={(e) => handleInputChange('partyName', e.target.value)}
              className={`${sharedStyles.input} ${errors.partyName ? 'error' : ''}`}
              placeholder="Enter customer name"
              required
            />
            {errors.partyName && (
              <PlatformText variant="caption" className="error-text">
                {errors.partyName}
              </PlatformText>
            )}
          </div>
        </div>

        {/* Quantity Input */}
        <div className={sharedStyles.field}>
          <label htmlFor="quantity-input" className={sharedStyles.formLabel}>
            <PlatformText variant="body" className={sharedStyles.labelText}>
              Quantity to Remove *
            </PlatformText>
          </label>
          <div className="quantity-input-container">
            <input
              id="quantity-input"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              className={`${sharedStyles.input} ${errors.quantity ? 'error' : ''}`}
              placeholder="Enter quantity"
              min="0.01"
              max={getCurrentStock()}
              step="0.01"
              required
            />
            <span className="unit-indicator">
              {getUnit()}
            </span>
          </div>
          {errors.quantity && (
            <PlatformText variant="caption" className="error-text">
              {errors.quantity}
            </PlatformText>
          )}
          {formData.quantity && !errors.quantity && (
            <PlatformText variant="caption" className="info-text">
              Remaining stock will be: {getRemainingStock()} {getUnit()}
            </PlatformText>
          )}
        </div>

        {/* Reason for Stock Out */}
        <div className={sharedStyles.field}>
          <label htmlFor="reason-input" className={sharedStyles.formLabel}>
            <PlatformText variant="body" className={sharedStyles.labelText}>
              Reason *
            </PlatformText>
          </label>
          <select
            id="reason-input"
            value={formData.reason}
            onChange={(e) => handleInputChange('reason', e.target.value)}
            className={`${sharedStyles.select} ${errors.reason ? 'error' : ''}`}
            required
          >
            <option value="">Select reason...</option>
            <option value="order_fulfillment">Order Fulfillment</option>
            <option value="damage">Damaged/Defective</option>
            <option value="adjustment">Stock Adjustment</option>
            <option value="other">Other</option>
          </select>
          {errors.reason && (
            <PlatformText variant="caption" className="error-text">
              {errors.reason}
            </PlatformText>
          )}
        </div>

        {/* Notes */}
        <div className={sharedStyles.field}>
          <label htmlFor="notes-input" className={sharedStyles.formLabel}>
            <PlatformText variant="body" className={sharedStyles.labelText}>
              Notes
            </PlatformText>
          </label>
          <textarea
            id="notes-input"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className={sharedStyles.textarea}
            placeholder="Add any additional notes..."
            rows={3}
          />
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className={sharedStyles.errorMessage}>
            <PlatformText variant="caption" className="error-text">
              {errors.submit}
            </PlatformText>
          </div>
        )}

        {/* Form Actions */}
        <div className={sharedStyles.actions}>
          <PlatformButton
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className={sharedStyles.cancel}
          >
            Cancel
          </PlatformButton>
          
          <PlatformButton
            type="submit"
            disabled={isSubmitting || !formData.productId || !formData.quantity || 
                     !formData.reason || (selectedProduct?.hasVariants && !formData.variantId)}
            className={sharedStyles.save}
          >
            {isSubmitting ? 'Removing Stock...' : 'Remove Stock'}
          </PlatformButton>
        </div>
      </form>
    </div>
  );
};

export default StockOutForm; 